import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import { dispatchProjectNotification } from '../../../../../../utils/projectNotification'
import AttachIcon from '../../../../../../assets/icon/AttachIcon'
import CommentIcon from '../../../../../../assets/icon/CommentIcon'
import RelatedIcon from '../../../../../../assets/icon/RelatedIcon'
import CalendarSmallIcon from '../../../../../../assets/icon/CalendarSmallIcon'
import CheckIcon from '../../../../../../assets/icon/CheckIcon'
import EditIcon from '../../../../../../assets/icon/EditIcon'
import FolderIcon from '../../../../../../assets/icon/FolderIcon'
import DotsVerticalIcon from '../../../../../../assets/icon/DotsVerticalIcon'
import DuplicateIcon from '../../../../../../assets/icon/DuplicateIcon'
import CopyLinkIcon from '../../../../../../assets/icon/CopyLinkIcon'
import TaskMoveIcon from '../../../../../../assets/icon/TaskMoveIcon'
import ArchiveIcon from '../../../../../../assets/icon/ArchiveIcon'
import TrashIcon from '../../../../../../assets/icon/TrashIcon'
import ActionCheckIcon from '../../../../../../assets/icon/ActionCheckIcon'
import CloseIcon from '../../../../../../assets/icon/CloseIcon'
import { Task } from "../../../../../../types/task"
import { Project } from "../../../../../../types"
import { hexToRGB } from "../../../../../../utils/hexToRGBA"
import TaskModal from "../../../../../../components/Modal/components/TaskModal"
import TaskService from "../../../../../../services/task.service"
import ProjectService from "../../../../../../services/project.service"
import DropDown from "../../../../../../components/DropDown"
import ProjectDeleteModal from "../../../../../../components/ProjectControls/ProjectDeleteModal"
import TaskMoveModal from "../../../../../../components/ProjectControls/TaskMoveModal"
import Avatar from '../../../../../../components/Avatar'
import { TaskDropTarget } from '../../../../hooks/useTaskDragAndDrop'
import { buildTaskUpdatePayload, updateTaskChangedFields } from '../../../../utils/taskPayload'
import { getProjectTaskStatus, getProjectTaskStatuses } from '../../../../utils/taskStatuses'
import { ListViewColumnState } from '../../../../utils/listViewColumns'
import { getTaskCreator, getTaskUsers, getUserDisplayName, getUserRoleLabel } from '../../../../utils/taskUsers'
import TimeLogService from '../../../../../../services/time-log.service'
import { stripTaskTimeTrackingFields } from '../../../../utils/taskPayload'
import { moveTaskToProject } from '../../../../utils/taskMove'
import { copyProjectTaskLink } from '../../../../../../utils/projectLink'
import TaskEstimatePicker from '../../../../../../components/Pickers/TaskEstimatePicker'
import TaskPriorityPicker from '../../../../../../components/Pickers/TaskPriorityPicker'
import UsersPicker from '../../../../../../components/Pickers/UsersPicker'
import TimeTrackerPicker from '../../../../../../components/Pickers/TimeTrackerPicker'
import { formatTaskDateTime } from '../../../../../../utils/formatDate'
import { getTaskType } from '../../../../../../components/Modal/components/TaskModal/utils/taskTypes'
import OverflowTooltip from '../../../../../../components/OverflowTooltip'
import { persistRelatedTask } from '../../../../../../utils/persistRelatedTask'
import { formatTaskTotalSpentTime, getTaskSpentHours } from '../../../../../../utils/taskSpentTime'
import TimeIcon from '../../../../../../assets/icon/TimeIcon'

interface ListItemProps {
    task: Task & { relatedTasks?: Array<any | null>, users?: any[] }
    parentTitle?: string
    project: Project
    projectUsers?: any[]
    visibleColumns: ListViewColumnState
    isSelected: boolean
    onToggle: () => void
    getTasks: () => void
    onTaskUpdated?: (task: Task | null, options?: { remove?: boolean }) => void
    onTaskOpen?: (task: Task) => void
    dragPreviewPosition?: TaskDropTarget['position'] | null
    draggable?: boolean
    isDragging?: boolean
    isPending?: boolean
    onDragEnd?: () => void
    onDragStart?: (taskId: number) => void
    onDrop?: (target?: TaskDropTarget | null) => Promise<void> | void
    onHover?: (target: TaskDropTarget) => void
}

let transparentDragImage: HTMLCanvasElement | null = null

function getTransparentDragImage() {
    if (typeof document === 'undefined') {
        return null
    }

    if (!transparentDragImage) {
        transparentDragImage = document.createElement('canvas')
        transparentDragImage.width = 1
        transparentDragImage.height = 1
    }

    return transparentDragImage
}

export default function ListItem({
    task,
    parentTitle,
    project,
    projectUsers = [],
    visibleColumns,
    isSelected,
    onToggle,
    getTasks,
    onTaskUpdated,
    onTaskOpen,
    dragPreviewPosition = null,
    draggable = true,
    isDragging = false,
    isPending = false,
    onDragEnd,
    onDragStart,
    onDrop,
    onHover,
}: ListItemProps) {
    const [isShowTask, setIsShowTask] = useState<any>(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isMoveOpen, setIsMoveOpen] = useState(false)
    const [renameTitle, setRenameTitle] = useState(task.title || '')
    const [localSpendTime, setLocalSpendTime] = useState<number | null>(() => getTaskSpentHours(task))
    const [assigneeDraft, setAssigneeDraft] = useState<Array<string | number> | null>(null)
    const assigneeDraftRef = useRef<Array<string | number> | null>(null)
    const assigneeSaveQueueRef = useRef<Promise<void>>(Promise.resolve())
    const assigneeRevisionRef = useRef(0)
    const assigneeSavedRevisionRef = useRef(0)
    const renameInputRef = useRef<HTMLInputElement>(null)
    const baseTaskProject = useMemo(() => {
        const taskProjectId = task?.project_id ?? task?.project?.id

        if (taskProjectId == null) {
            return task?.project || project
        }

        const isContextProject = String(project?.id) === String(taskProjectId)
        if (isContextProject) {
            return {
                ...project,
                ...(task?.project || {}),
                id: taskProjectId,
                name: task?.project_name || task?.project?.name || project?.name,
                statuses: task?.project?.statuses?.length ? task.project.statuses : project?.statuses,
                task_statuses: task?.project?.task_statuses?.length ? task.project.task_statuses : project?.task_statuses,
            }
        }

        return task?.project || {
            id: taskProjectId,
            name: task?.project_name || 'Проект',
        }
    }, [project, task?.project, task?.project_id, task?.project_name])
    const [detailedTaskProject, setDetailedTaskProject] = useState<any>(null)
    const taskProject = detailedTaskProject || baseTaskProject
    const isArchived = Boolean(task.is_archived || task.archived_at)
    const taskUsers = getTaskUsers(task, projectUsers)
    const taskCreator = getTaskCreator(task, projectUsers)
    const taskAssigneeIds = Array.isArray(task.assignee_ids)
        ? task.assignee_ids
        : taskUsers.map((user: any) => user?.id ?? user?.user_id).filter(Boolean)

    useEffect(() => {
        if (assigneeDraftRef.current === null || assigneeSavedRevisionRef.current !== assigneeRevisionRef.current) return
        const selectedIds = assigneeDraftRef.current.map(String).sort()
        const savedIds = taskAssigneeIds.map(String).sort()
        if (selectedIds.length === savedIds.length && selectedIds.every((id, index) => id === savedIds[index])) {
            assigneeDraftRef.current = null
            setAssigneeDraft(null)
        }
    }, [taskAssigneeIds])

    useEffect(() => {
        setRenameTitle(task.title || '')
    }, [task.title])

    useEffect(() => {
        let isMounted = true
        const projectId = baseTaskProject?.id

        setDetailedTaskProject(null)
        if (projectId == null || getProjectTaskStatuses(baseTaskProject).length) {
            return () => {
                isMounted = false
            }
        }

        void ProjectService.getProjectWithStatuses(baseTaskProject)
            .then((detailedProject) => {
                if (isMounted) {
                    setDetailedTaskProject(detailedProject)
                }
            })
            .catch((error) => console.error('Failed to load task project details:', error))

        return () => {
            isMounted = false
        }
    }, [baseTaskProject])

    const taskSpendTime = getTaskSpentHours(task)

    useEffect(() => {
        setLocalSpendTime(taskSpendTime)
    }, [taskSpendTime])

    useEffect(() => {
        let isMounted = true

        const loadTimeLog = async () => {
            if (!visibleColumns.timeTracking || !task.id || taskSpendTime != null) {
                return
            }

            try {
                const timeLog = await TimeLogService.getByTaskId(task.id)
                const minutesSpent = Number(timeLog?.minutes_spent)

                if (isMounted && timeLog && !Number.isNaN(minutesSpent)) {
                    setLocalSpendTime(minutesSpent / 60)
                }
            } catch (error) {
                console.error('Failed to load task time log:', error)
            }
        }

        void loadTimeLog()

        return () => {
            isMounted = false
        }
    }, [task.id, visibleColumns.timeTracking, taskSpendTime])

    useEffect(() => {
        if (isRenaming) {
            setTimeout(() => renameInputRef.current?.focus(), 50)
        }
    }, [isRenaming])

    const updateTask = async (taskData: any) => {
        await TaskService.update(task.id, stripTaskTimeTrackingFields(taskData))

        await Promise.all((taskData.deletedRelatedTaskIds || []).map((relatedTaskId: number | string) => (
            TaskService.delete(Number(relatedTaskId))
        )))

        await Promise.all((taskData.relatedTasks || []).filter(Boolean).map((currentTask: any) => (
            persistRelatedTask({
                task: currentTask,
                parentTaskId: taskData.id,
                projectId: taskProject.id,
            })
        )))

        setIsShowTask(false)
        await getTasks()
    }

    const startRename = () => {
        setRenameTitle(task.title || '')
        setIsRenaming(true)
    }

    const isControlTarget = (target: EventTarget | null) => {
        return target instanceof HTMLElement && Boolean(target.closest('[data-list-task-control="true"]'))
    }

    const updateTaskField = async (overrides: Record<string, any>) => {
        try {
            const updatedTask = await updateTaskChangedFields(task.id, taskProject.id, overrides)
            onTaskUpdated?.(updatedTask)
            await getTasks()
        } catch (error) {
            console.error('Failed to update task field:', error)
        }
    }

    const setDueDate = (dueDate: Date) => {
        void updateTaskField({ due_date: dueDate })
    }

    const setPriority = (priority: string | number) => {
        void updateTaskField({ priority })
    }

    const setStatus = (status: string) => {
        void updateTaskField({ status })
    }

    const setUsers = (userId: string | number) => {
        const currentIds = assigneeDraftRef.current ?? taskAssigneeIds
        const nextAssigneeIds = currentIds.some((currentUserId: string | number) => String(currentUserId) === String(userId))
            ? currentIds.filter((currentUserId: string | number) => String(currentUserId) !== String(userId))
            : [...currentIds, userId]
        assigneeDraftRef.current = nextAssigneeIds
        setAssigneeDraft(nextAssigneeIds)
        const revision = ++assigneeRevisionRef.current

        assigneeSaveQueueRef.current = assigneeSaveQueueRef.current.catch(() => undefined).then(async () => {
            try {
                const updatedTask = await updateTaskChangedFields(task.id, taskProject.id, {
                    assignee_ids: nextAssigneeIds,
                })
                if (revision !== assigneeRevisionRef.current) return
                assigneeSavedRevisionRef.current = revision
                if (onTaskUpdated) onTaskUpdated(updatedTask)
                else await getTasks()
            } catch (error) {
                console.error('Failed to update task assignees:', error)
                if (revision !== assigneeRevisionRef.current) return
                assigneeDraftRef.current = null
                setAssigneeDraft(null)
                dispatchProjectNotification({ text: 'Не удалось обновить участников задачи', type: 'error' })
                await getTasks()
            }
        })
    }

    const setTrackedTime = async (spendTime: number) => {
        try {
            await TimeLogService.syncForTask({
                taskId: task.id,
                spendTime,
            })
            setLocalSpendTime(spendTime)
            await getTasks()
        } catch (error) {
            console.error('Failed to update task time tracking:', error)
        }
    }

    const confirmRename = async () => {
        if (!renameTitle.trim()) return

        try {
            const updatedTask = await updateTaskChangedFields(task.id, taskProject.id, {
                title: renameTitle.trim(),
            })
            onTaskUpdated?.(updatedTask)
            setIsRenaming(false)
            await getTasks()
        } catch (error) {
            console.error('Failed to rename task:', error)
        }
    }

    const duplicateTask = async () => {
        try {
            const duplicatedTask = await TaskService.create(buildTaskUpdatePayload(task, taskProject.id, {
                title: `${task.title || 'Задача'} (копия)`,
            }))

            const relatedTasks = Array.isArray(task.relatedTasks)
                ? task.relatedTasks.filter((currentTask) => !!currentTask)
                : []

            await Promise.all(relatedTasks.map((relatedTask: any) => TaskService.create(buildTaskUpdatePayload(relatedTask, taskProject.id, {
                parent_id: duplicatedTask.id,
            }))))

            await getTasks()

            dispatchProjectNotification({
                text: 'Задача успешно продублирована',
                action: {
                    label: 'Отменить',
                    onClick: () => {
                        void (async () => {
                            try {
                                await TaskService.delete(duplicatedTask.id)
                                await getTasks()
                            } catch (error) {
                                console.error('Failed to undo task duplication:', error)
                            }
                        })()
                    },
                },
            })
        } catch (error) {
            console.error('Failed to duplicate task:', error)
        }
    }

    const copyTaskLink = async () => {
        try {
            await copyProjectTaskLink(taskProject.id, task.id, 'list')
            dispatchProjectNotification({
                text: 'Ссылка скопирована',
            })
        } catch (error) {
            console.error('Failed to copy task link:', error)
        }
    }

    const moveTask = async (targetProject: Project) => {
        try {
            await moveTaskToProject(task, taskProject.id, targetProject)
            onTaskUpdated?.(task, { remove: true })
            setIsMoveOpen(false)
            await getTasks()

            dispatchProjectNotification({
                text: 'Задача перемещена в другой проект',
            })
        } catch (error) {
            console.error('Failed to move task:', error)
        }
    }

    const deleteTask = async () => {
        try {
            await TaskService.delete(task.id)
            onTaskUpdated?.(task, { remove: true })
            setIsDeleteOpen(false)
            await getTasks()
        } catch (error) {
            console.error('Failed to delete task:', error)
        }
    }

    const archiveTask = async () => {
        try {
            const wasArchived = isArchived
            const updatedTask = wasArchived
                ? await TaskService.unarchive(task.id)
                : await TaskService.archive(task.id)

            onTaskUpdated?.(updatedTask)
            await getTasks()

            if (!wasArchived) {
                dispatchProjectNotification({
                    text: 'Задача перенесена в архив',
                    action: {
                        label: 'Отменить',
                        onClick: () => {
                            void (async () => {
                                try {
                                    const restored = await TaskService.unarchive(task.id)
                                    onTaskUpdated?.(restored)
                                    getTasks()
                                } catch (error) {
                                    console.error('Failed to undo task archive:', error)
                                }
                            })()
                        },
                    },
                })
            }
        } catch (error) {
            console.error('Failed to change task archive state:', error)
        }
    }

    const statusOption = getProjectTaskStatus(taskProject, task.status)
    const projectStatuses = getProjectTaskStatuses(taskProject)
    const statusStyles = statusOption?.color || '#B1AEC7'
    const statusTitle = statusOption?.title || task.status
    const createdAtTitle = formatTaskDateTime(task.created_at)
    const creatorName = getUserDisplayName(taskCreator) || '-'
    const creatorRole = getUserRoleLabel(taskCreator)
    const taskTypeOption = getTaskType((task as any).task_type)
    const totalTimeSpentTitle = formatTaskTotalSpentTime(task)

    const openLocalTask = async () => {
        if (taskProject?.id != null && getProjectTaskStatuses(taskProject).length === 0) {
            try {
                const detailedProject = await ProjectService.getProjectWithStatuses(taskProject)
                setDetailedTaskProject(detailedProject)
            } catch (error) {
                console.error('Failed to load project before opening task:', error)
            }
        }

        let taskToOpen = task
        const hasStages = Array.isArray(task.relatedTasks) && task.relatedTasks.some(Boolean)
        const stageCount = Number(task.subtasks_count ?? (task as any).children_count ?? 0)

        if (!hasStages && stageCount > 0 && taskProject?.id != null) {
            try {
                const projectTasks = await ProjectService.getAllProjectTasks(String(taskProject.id))
                const detailedTask = projectTasks.tasks.find((projectTask: any) => (
                    String(projectTask.id) === String(task.id)
                ))

                if (detailedTask) {
                    taskToOpen = {
                        ...task,
                        ...detailedTask,
                    }
                }
            } catch (error) {
                console.error('Failed to load task stages before opening task:', error)
            }
        }

        setIsShowTask(taskToOpen)
    }

    return (
        <div
            className={`${styles.listItem} ${isSelected ? styles.listItemSelected : ''} ${isDragging ? styles.listItemDragging : ''} ${isPending ? styles.listItemPending : ''} ${dragPreviewPosition === 'before' ? styles.listItemDropBefore : ''} ${dragPreviewPosition === 'after' ? styles.listItemDropAfter : ''}`}
            draggable={draggable && !isRenaming}
            onClick={(event) => {
                if (isControlTarget(event.target)) {
                    return
                }

                if (!isRenaming && !isDragging) {
                    if (onTaskOpen) {
                        onTaskOpen(task)
                    } else {
                        void openLocalTask()
                    }
                }
            }}
            onDragEnd={onDragEnd}
            onDragOver={(event) => {
                if (!onHover) {
                    return
                }

                event.preventDefault()
                event.stopPropagation()

                const rect = event.currentTarget.getBoundingClientRect()
                const isAfter = event.clientY - rect.top > rect.height / 2

                onHover({
                    mode: 'list',
                    position: isAfter ? 'after' : 'before',
                    status: '',
                    taskId: task.id,
                })
            }}
            onDragEnter={(event) => {
                if (!onHover) {
                    return
                }

                event.preventDefault()
                event.stopPropagation()

                const rect = event.currentTarget.getBoundingClientRect()
                const isAfter = event.clientY - rect.top > rect.height / 2

                onHover({
                    mode: 'list',
                    position: isAfter ? 'after' : 'before',
                    status: '',
                    taskId: task.id,
                })
            }}
            onDragStart={(event) => {
                if (isControlTarget(event.target)) {
                    event.preventDefault()
                    event.stopPropagation()
                    return
                }

                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.setData('text/plain', String(task.id))

                const dragImage = getTransparentDragImage()
                if (dragImage) {
                    event.dataTransfer.setDragImage(dragImage, 0, 0)
                }

                requestAnimationFrame(() => {
                    onDragStart?.(task.id)
                })
            }}
            onDrop={(event) => {
                if (!onDrop) {
                    return
                }

                event.preventDefault()
                event.stopPropagation()

                const rect = event.currentTarget.getBoundingClientRect()
                const isAfter = event.clientY - rect.top > rect.height / 2

                void onDrop({
                    mode: 'list',
                    position: isAfter ? 'after' : 'before',
                    status: '',
                    taskId: task.id,
                })
            }}
        >
            <div className={styles.listItem__checkbox}>
                <div
                    className={`${styles.listItem__checkboxBox} ${isSelected ? styles.listItem__checkboxBoxActive : ''}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        onToggle()
                    }}
                >
                    {isSelected && <CheckIcon />}
                </div>
            </div>
            <div className={styles.listItem__content}>
                {isRenaming ? (
                    <div
                        className={styles.listItem__renameWrap}
                        data-list-task-control="true"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <input
                            ref={renameInputRef}
                            className={styles.listItem__renameInput}
                            value={renameTitle}
                            onChange={(e) => setRenameTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') confirmRename()
                                if (e.key === 'Escape') setIsRenaming(false)
                            }}
                        />
                        <button
                            type="button"
                            className={styles.listItem__renameConfirm}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation()
                                void confirmRename()
                            }}
                        >
                            <ActionCheckIcon />
                        </button>
                        <button
                            type="button"
                            className={styles.listItem__renameCancel}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation()
                                setIsRenaming(false)
                            }}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                ) : (
                    <>
                        <OverflowTooltip text={task.title || ''} className={styles.listItem__title} />
                        {task.parent_id && <span className={styles.listItem__parent} title={parentTitle || String(task.parent_id)}>↳ {parentTitle || 'Этап'}</span>}
                        {!!onTaskUpdated && (
                            <div
                                className={styles.listItem__editIcon}
                                data-list-task-control="true"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    startRename()
                                }}
                            >
                                <EditIcon />
                            </div>
                        )}
                    </>
                )}
                <div className={styles.listItem__meta}>
                    <div className={styles.listItem__metaItem}>
                        <AttachIcon />
                        <span>{task.attachments_count || 0}</span>
                    </div>
                    <div className={styles.listItem__metaItem}>
                        <CommentIcon />
                        <span>{task.comments_count || 0}</span>
                    </div>
                    <div className={styles.listItem__metaItem}>
                        <RelatedIcon />
                        <span>{task.subtasks_count || 0}</span>
                    </div>
                </div>
            </div>
            {visibleColumns.dueDate && (
                <div
                    className={styles.listItem__date}
                    data-list-task-control="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className={styles.listItem__fieldControl}>
                        <TaskEstimatePicker
                            setEstimate={setDueDate}
                            estimate={task.due_date as any}
                            dropdownPosition="right"
                        />
                    </div>
                </div>
            )}
            {visibleColumns.priority && (
                <div
                    className={styles.listItem__priority}
                    data-list-task-control="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className={styles.listItem__fieldControl}>
                        <TaskPriorityPicker
                            getPriority={setPriority}
                            priorityId={task.priority}
                            dropdownPosition="right"
                        />
                    </div>
                </div>
            )}
            {visibleColumns.status && (
                <div
                    className={styles.listItem__status}
                    data-list-task-control="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <DropDown
                        closeDropDownByPick
                        position="right"
                        btn={(
                            <div className={styles.listItem__statusLabel} style={{ background: hexToRGB(statusStyles, .16) }}>
                                <span className={styles.listItem__statusDot} style={{ background: statusStyles }} />
                                <OverflowTooltip text={statusTitle || ''} />
                            </div>
                        )}
                    >
                        <div className={styles.listItem__statusDropdown}>
                            {projectStatuses.map((status) => (
                                <div
                                    key={status.key}
                                    className={`${styles.listItem__statusDropdownItem} ${task.status === status.key ? styles.listItem__statusDropdownItemActive : ''}`}
                                    onClick={() => setStatus(status.key)}
                                >
                                    <span className={styles.listItem__statusDot} style={{ background: status.color }} />
                                    <span>{status.title}</span>
                                </div>
                            ))}
                        </div>
                    </DropDown>
                </div>
            )}
            {visibleColumns.users && (
                <div
                    className={styles.listItem__users}
                    data-list-task-control="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className={styles.listItem__fieldControl}>
                        <UsersPicker
                            getUsers={setUsers}
                            usersId={assigneeDraft ?? taskAssigneeIds}
                            selectedUsers={taskUsers}
                            availableUsers={String(taskProject?.id) === String(project?.id) ? projectUsers : undefined}
                            projectId={taskProject?.id}
                            dropdownPosition="right"
                        />
                    </div>
                </div>
            )}
            {visibleColumns.project && (
                <div className={styles.listItem__project}>
                    <div className={styles.listItem__projectLabel}>
                        <FolderIcon />
                        <OverflowTooltip text={taskProject?.name || '-'} />
                    </div>
                </div>
            )}
            {visibleColumns.taskType && (
                <div className={styles.listItem__taskType}>
                    <div className={styles.listItem__taskTypeLabel}>
                        {taskTypeOption.icon}
                        <OverflowTooltip text={taskTypeOption.label} />
                    </div>
                </div>
            )}
            {visibleColumns.createdAt && (
                <div className={styles.listItem__createdAt}>
                    <div className={styles.listItem__createdAtLabel}>
                        <CalendarSmallIcon />
                        <span>{createdAtTitle}</span>
                    </div>
                </div>
            )}
            {visibleColumns.creator && (
                <div className={styles.listItem__creator}>
                    <div className={styles.listItem__creatorWrap}>
                        <div className={styles.listItem__creatorAvatar}>
                            <Avatar user={taskCreator} />
                        </div>
                        <span className={styles.listItem__creatorInfo}>
                            <OverflowTooltip text={creatorName} className={styles.listItem__creatorName} />
                            {creatorRole && <OverflowTooltip text={creatorRole} className={styles.listItem__creatorRole} />}
                        </span>
                    </div>
                </div>
            )}
            {visibleColumns.timeTracking && (
                <div
                    className={`${styles.listItem__timeTracking} ${!visibleColumns.totalTimeSpent ? styles.listItem__lastColumn : ''}`}
                    data-list-task-control="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className={styles.listItem__fieldControl}>
                        <TimeTrackerPicker
                            getSpendTime={setTrackedTime}
                            spendTime={localSpendTime}
                            dropdownPosition="right"
                        />
                    </div>
                </div>
            )}
            {visibleColumns.totalTimeSpent && (
                <div
                    className={styles.listItem__totalTimeSpent}
                    data-list-task-control="true"
                    aria-disabled="true"
                >
                    <div className={styles.listItem__totalTimeSpentLabel} title="Общее время — только для просмотра">
                        <TimeIcon color="#B1AEC7" size={16} />
                        <OverflowTooltip text={totalTimeSpentTitle} />
                    </div>
                </div>
            )}
            <div
                className={styles.listItem__actions}
                data-list-task-control="true"
                onClick={(event) => event.stopPropagation()}
            >
                {!!onTaskUpdated && (
                    <DropDown
                        closeDropDownByPick
                        position="right"
                        btn={(
                            <div className={styles.listItem__actionsBtn}>
                                <DotsVerticalIcon />
                            </div>
                        )}
                    >
                        <div className={styles.listItem__dropdown}>
                            <div className={styles.listItem__dropdownItem} onClick={startRename}>
                                <EditIcon />
                                <span>Переименовать</span>
                            </div>
                            <div className={styles.listItem__dropdownItem} onClick={duplicateTask}>
                                <DuplicateIcon />
                                <span>Дублировать</span>
                            </div>
                            <div className={styles.listItem__dropdownItem} onClick={copyTaskLink}>
                                <span className={styles.listItem__dropdownIconLarge}>
                                    <CopyLinkIcon />
                                </span>
                                <span>Копировать ссылку</span>
                            </div>
                            <div className={styles.listItem__dropdownItem} onClick={() => setIsMoveOpen(true)}>
                                <span className={styles.listItem__dropdownMoveIcon}>
                                    <TaskMoveIcon />
                                </span>
                                <span>Переместить</span>
                            </div>
                            <div className={styles.listItem__dropdownItem} onClick={archiveTask}>
                                <ArchiveIcon />
                                <span>{isArchived ? 'Вернуть с архива' : 'Архивировать'}</span>
                            </div>
                            <div className={`${styles.listItem__dropdownItem} ${styles.listItem__dropdownItemDanger}`} onClick={() => setIsDeleteOpen(true)}>
                                <TrashIcon />
                                <span>Удалить</span>
                            </div>
                        </div>
                    </DropDown>
                )}
            </div>
            <TaskMoveModal
                currentProjectId={taskProject.id}
                isOpen={isMoveOpen}
                onClose={() => setIsMoveOpen(false)}
                onConfirm={moveTask}
            />
            <ProjectDeleteModal
                isOpen={isDeleteOpen}
                title="Удалить задачу?"
                text={`Вы уверены, что хотите удалить задачу «${task.title || 'Без названия'}»? Это действие нельзя отменить.`}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={deleteTask}
            />
            {!onTaskOpen && isShowTask && (
                <TaskModal
                    isOpen={isShowTask}
                    onClose={() => setIsShowTask(false)}
                    task={isShowTask}
                    project={taskProject}
                    createTask={updateTask}
                    onEditCommitted={getTasks}
                />
            )}
        </div>
    )
}
